import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ANNOUNCEMENT_DETAILS_MAX_LENGTH,
  ANNOUNCEMENT_LINK_MAX_LENGTH,
  ANNOUNCEMENT_LOCATION_MAX_LENGTH,
  ANNOUNCEMENT_TITLE_MAX_LENGTH,
  AnnouncementSchema,
  CreateAnnouncementRequestSchema,
  type AnnouncementDto,
  type AnnouncementKind,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure } from '../finance/detail';
import { messageOf } from './leave';

const ANNOUNCEMENTS = '/api/v1/hr/announcements';
export const ANNOUNCEMENT = 'hr.announcement';

const KIND_LABELS: Record<AnnouncementKind, string> = { event: 'Event', news: 'News' };

/** "16 Oct 2026", or "16 – 18 Oct 2026" for a span. */
export function dateSpan(start: string, end: string | null): string {
  const format = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' }).format(
      new Date(`${date}T00:00:00Z`),
    );
  const full = { day: 'numeric', month: 'short', year: 'numeric' } as const;
  if (end === null || end === start) return format(start, full);
  return `${format(start, { day: 'numeric', month: 'short' })} – ${format(end, full)}`;
}

/**
 * Company announcements (`/hr/announcements`; CLR-63, FRD-HRP-105): HR or an administrator posts
 * TAQON events and news, which staff see on their dashboard. Withdrawn, never deleted.
 */
export function AnnouncementsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'hr.announcement:create');
  const [kind, setKind] = useState<AnnouncementKind | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('active');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const query = [
    'pageSize=200',
    'sort=-start_date',
    ...(kind === '' ? [] : [`filter[kind]=${kind}`]),
    ...(status === '' ? [] : [`filter[status]=${status}`]),
  ].join('&');
  const list = useQuery({
    queryKey: ['hr', 'announcements', kind, status],
    queryFn: () => getPage(`${ANNOUNCEMENTS}?${query}`, AnnouncementSchema),
  });
  const rows = list.data?.items ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Announcements"
        description="TAQON events and news. Everyone who may see announcements finds them on their dashboard."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              Add announcement
            </Button>
          ) : undefined
        }
      />
      <Section
        title="Announcements"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as AnnouncementKind | '');
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="event">Events</MenuItem>
              <MenuItem value="news">News</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as 'active' | 'inactive' | '');
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Withdrawn</MenuItem>
            </TextField>
          </Stack>
        }
      >
        {list.isPending && <LoadingState label="Loading announcements" />}
        {list.isError && <ErrorState message={list.error.message} />}
        {list.data !== undefined && rows.length === 0 && (
          <EmptyState
            title="No announcements"
            {...(canCreate ? { detail: 'Add an event or a news item for staff to see.' } : {})}
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Announcements">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {dateSpan(a.startDate, a.kind === 'event' ? a.endDate : null)}
                  </TableCell>
                  <TableCell>{KIND_LABELS[a.kind]}</TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={`/hr/announcements/${a.id}`}>
                      {a.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={a.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {creating && (
        <AnnouncementDialog
          onClose={(created) => {
            setCreating(false);
            if (created !== undefined) void navigate(`/hr/announcements/${created.id}`);
          }}
        />
      )}
    </Stack>
  );
}

/** One announcement (`/hr/announcements/:id`): its details, edit and withdraw, and its picture. */
export function AnnouncementDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'hr.announcement:change');
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const detail = useQuery({
    queryKey: ['hr', 'announcement', id],
    queryFn: () => getData(`${ANNOUNCEMENTS}/${id}`, AnnouncementSchema),
  });
  const back = (
    <Button component={RouterLink} to="/hr/announcements" startIcon={<ArrowBackIcon />}>
      Announcements
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the announcement" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Announcement" noun="announcement" error={detail.error} back={back} />
    );
  }
  const a = detail.data;
  const event = a.kind === 'event';
  return (
    <Stack spacing={3}>
      <PageHeader
        title={a.title}
        description={`${KIND_LABELS[a.kind]} · ${dateSpan(a.startDate, event ? a.endDate : null)}`}
        actions={
          <Stack direction="row" spacing={1}>
            {back}
            {canChange && (
              <>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.currentTarget.focus();
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color={a.status === 'active' ? 'warning' : 'primary'}
                  onClick={(e) => {
                    e.currentTarget.focus();
                    setToggling(true);
                  }}
                >
                  {a.status === 'active' ? 'Withdraw' : 'Restore'}
                </Button>
              </>
            )}
          </Stack>
        }
      />
      <Section title="Details">
        <Facts
          label="Announcement details"
          facts={[
            ['Status', <StatusChip key="s" status={a.status} />],
            ['Kind', KIND_LABELS[a.kind]],
            [event ? 'First day' : 'Published', a.startDate],
            [event ? 'Last day' : 'Shown until', a.endDate ?? (event ? 'Same day' : '30 days')],
            ...(event
              ? ([
                  ['Location', a.location ?? '—'],
                  ['TAQON attending', a.taqonAttending ? 'Yes' : 'No'],
                ] as [string, string][])
              : []),
            ['Details', a.details ?? '—'],
            [
              'PDF',
              a.hasDocument === true ? (
                <Link
                  key="d"
                  href={`${ANNOUNCEMENTS}/${encodeURIComponent(a.id)}/document`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the PDF <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                </Link>
              ) : (
                '—'
              ),
            ],
            [
              'Link',
              a.linkUrl === null ? (
                '—'
              ) : (
                <Link
                  key="l"
                  href={a.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {a.linkUrl} <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                </Link>
              ),
            ],
          ]}
        />
      </Section>
      <Alert severity="info" variant="outlined">
        {event
          ? 'Attach a picture (JPEG, PNG or WebP) to show a photo with the event in Coming up and in the daily popup, and a PDF (an agenda or a brochure) to offer it there to open.'
          : 'Attach a picture (JPEG, PNG or WebP) to show a thumbnail on the dashboard, and a PDF to offer it to open.'}{' '}
        Select Upload below and choose the document type. The newest file of each kind that has
        passed the virus check is used.
      </Alert>
      <DocumentsPanel
        recordType={ANNOUNCEMENT}
        recordId={a.id}
        canAttach={canChange}
        title="Picture and documents"
      />
      <RecordHistory recordType={ANNOUNCEMENT} recordId={a.id} />
      {editing && (
        <AnnouncementDialog
          announcement={a}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
      {toggling && (
        <ToggleAnnouncement
          announcement={a}
          onClose={() => {
            setToggling(false);
          }}
        />
      )}
    </Stack>
  );
}

function AnnouncementDialog({
  announcement,
  onClose,
}: {
  announcement?: AnnouncementDto;
  onClose: (saved?: AnnouncementDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<AnnouncementKind>(announcement?.kind ?? 'event');
  const [title, setTitle] = useState(announcement?.title ?? '');
  const [details, setDetails] = useState(announcement?.details ?? '');
  const [startDate, setStartDate] = useState(announcement?.startDate ?? '');
  const [endDate, setEndDate] = useState(announcement?.endDate ?? '');
  const [linkUrl, setLinkUrl] = useState(announcement?.linkUrl ?? '');
  const [location, setLocation] = useState(announcement?.location ?? '');
  const [taqonAttending, setTaqonAttending] = useState(announcement?.taqonAttending ?? false);
  const event = kind === 'event';
  const body = {
    title: title.trim(),
    details: details.trim() === '' ? null : details.trim(),
    startDate,
    endDate: endDate === '' ? null : endDate,
    linkUrl: linkUrl.trim() === '' ? null : linkUrl.trim(),
    // An event's location and TAQON's attendance; news carries neither.
    location: event && location.trim() !== '' ? location.trim() : null,
    taqonAttending: event && taqonAttending,
  };
  const check = CreateAnnouncementRequestSchema.safeParse({ kind, ...body });
  const issue = (field: string) =>
    check.success ? undefined : check.error.issues.find((i) => i.path[0] === field)?.message;
  const endBeforeStart = endDate !== '' && startDate !== '' && endDate < startDate;
  const badLink = linkUrl.trim() !== '' && issue('linkUrl') !== undefined;
  const save = useMutation({
    mutationFn: () =>
      announcement === undefined
        ? postJson(
            ANNOUNCEMENTS,
            { kind, ...body },
            AnnouncementSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${ANNOUNCEMENTS}/${announcement.id}`,
            body,
            AnnouncementSchema,
            session.data?.csrfToken ?? '',
            announcement.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'announcements'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'announcement', saved.id] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'noticeboard'] });
      onClose(saved);
    },
  });
  return (
    <FormDialog
      open
      title={announcement === undefined ? 'Add an announcement' : `Edit ${announcement.title}`}
      submitLabel={announcement === undefined ? 'Add announcement' : 'Save changes'}
      canSubmit={check.success}
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {announcement === undefined && (
        <TextField
          select
          required
          label="Kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as AnnouncementKind);
          }}
          helperText={
            event
              ? 'A company event: a celebration, a meeting, an office closure'
              : 'News, with an optional link to the article on another website'
          }
        >
          <MenuItem value="event">Event</MenuItem>
          <MenuItem value="news">News</MenuItem>
        </TextField>
      )}
      <TextField
        required
        label="Title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: ANNOUNCEMENT_TITLE_MAX_LENGTH } }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          required
          fullWidth
          type="date"
          label={event ? 'First day' : 'Publication date'}
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          type="date"
          label={event ? 'Last day' : 'Shown until'}
          value={endDate}
          error={endBeforeStart}
          helperText={
            endBeforeStart
              ? 'Cannot be before the first date'
              : event
                ? 'Leave empty for a one-day event'
                : 'Leave empty to show it for 30 days'
          }
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      <TextField
        label="Details"
        multiline
        minRows={3}
        value={details}
        onChange={(e) => {
          setDetails(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: ANNOUNCEMENT_DETAILS_MAX_LENGTH } }}
      />
      <TextField
        label={event ? 'Link for more details (optional)' : 'Link to the article (optional)'}
        placeholder="https://"
        value={linkUrl}
        error={badLink}
        helperText={
          badLink
            ? 'Enter a full https:// address of a website'
            : event
              ? 'Shown as “More details” with the event on the dashboard; opens in a new tab.'
              : 'Opens in a new tab. TAQON Infinity never loads anything from that website itself.'
        }
        onChange={(e) => {
          setLinkUrl(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: ANNOUNCEMENT_LINK_MAX_LENGTH, inputMode: 'url' } }}
      />
      {event && (
        <TextField
          label="Location (optional)"
          placeholder="For example Dubai World Trade Centre, Hall 4"
          value={location}
          onChange={(e) => {
            setLocation(e.target.value);
          }}
          helperText="Where the event takes place. Shown with the event on the dashboard."
          slotProps={{ htmlInput: { maxLength: ANNOUNCEMENT_LOCATION_MAX_LENGTH } }}
        />
      )}
      {event && (
        <FormControlLabel
          control={
            <Checkbox
              checked={taqonAttending}
              onChange={(e) => {
                setTaqonAttending(e.target.checked);
              }}
            />
          }
          label="TAQON is attending this event"
        />
      )}
      <Alert severity="info" variant="outlined">
        {announcement === undefined
          ? 'After you add the announcement, open it to attach a picture or a PDF (an agenda or a brochure). Both are shown with it on the dashboard.'
          : 'To change the picture or the PDF, use the Picture and documents section on the announcement page.'}
      </Alert>
    </FormDialog>
  );
}

function ToggleAnnouncement({
  announcement,
  onClose,
}: {
  announcement: AnnouncementDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = announcement.status === 'active' ? 'inactive' : 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${ANNOUNCEMENTS}/${announcement.id}`,
        { status: next },
        AnnouncementSchema,
        session.data?.csrfToken ?? '',
        announcement.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'announcements'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'announcement', announcement.id] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'noticeboard'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`${next === 'inactive' ? 'Withdraw' : 'Restore'} ${announcement.title}?`}
      message={
        next === 'inactive'
          ? 'It will no longer appear on anyone’s dashboard.'
          : 'It will appear on dashboards again while its dates are current.'
      }
      confirmLabel={next === 'inactive' ? 'Withdraw' : 'Restore'}
      tone={next === 'inactive' ? 'warning' : 'primary'}
      busy={change.isPending}
      error={change.isError ? change.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        change.mutate();
      }}
    />
  );
}
